import { defineComponent as _defineComponent } from 'vue';
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives } from "vue";
const _hoisted_1 = {
    class: "pointer-events-none fixed inset-0 overflow-hidden",
    "aria-hidden": "true"
};
const _hoisted_2 = { class: "relative z-10 flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between" };
const _hoisted_3 = { class: "flex min-w-0 items-center gap-3" };
const _hoisted_4 = ["src", "alt"];
const _hoisted_5 = { class: "min-w-0" };
const _hoisted_6 = { class: "mt-1 text-3xl font-black tracking-tight sm:text-4xl" };
const _hoisted_7 = { class: "flex flex-col gap-1 sm:items-end" };
const _hoisted_8 = { class: "flex items-center gap-2" };
const _hoisted_9 = ["href"];
const _hoisted_10 = ["onClick"];
const _hoisted_11 = { class: "flex flex-col gap-5 p-5 sm:p-8" };
const _hoisted_12 = {
    key: 0,
    class: "flex flex-col items-center gap-3 py-16 text-center text-sm text-eblack-500",
    role: "status"
};
const _hoisted_13 = {
    key: 1,
    class: "rounded-2xl bg-red-50 p-4 text-sm font-medium text-red-700"
};
const _hoisted_14 = {
    key: 0,
    class: "itinerary-review-fade rounded-2xl bg-red-50 p-4 text-sm font-medium text-red-700"
};
const _hoisted_15 = { class: "flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" };
const _hoisted_16 = { class: "text-xs font-bold uppercase tracking-[0.28em] text-eblack-400" };
const _hoisted_17 = { class: "mt-2 text-3xl font-black tracking-tight" };
const _hoisted_18 = {
    key: 0,
    class: "mt-1 text-sm font-medium text-eblack-500"
};
const _hoisted_19 = { class: "text-sm font-bold" };
const _hoisted_20 = { class: "mt-4 grid gap-3 text-sm text-eblack-600 sm:grid-cols-2" };
const _hoisted_21 = { class: "font-semibold text-eblack-800" };
const _hoisted_22 = { class: "mt-2 block text-eblack-400" };
const _hoisted_23 = { class: "flex items-start justify-between gap-4" };
const _hoisted_24 = { class: "text-sm font-bold" };
const _hoisted_25 = { class: "mt-1 text-xs text-eblack-400" };
const _hoisted_26 = { class: "mt-4 flex flex-col gap-4" };
const _hoisted_27 = { class: "flex items-center justify-between gap-3 text-sm font-semibold" };
const _hoisted_28 = { class: "mt-3 flex flex-col gap-2 text-sm text-eblack-500" };
const _hoisted_29 = { class: "flex justify-between gap-3" };
const _hoisted_30 = { class: "flex justify-between gap-3" };
const _hoisted_31 = ["href"];
const _hoisted_32 = { class: "mt-1 text-eblack-500" };
const _hoisted_33 = ["disabled"];
const _hoisted_34 = {
    key: 5,
    class: "itinerary-review-fade flex flex-col gap-3"
};
const _hoisted_35 = ["placeholder"];
const _hoisted_36 = { class: "flex flex-col gap-2 sm:flex-row" };
const _hoisted_37 = ["disabled"];
const _hoisted_38 = ["disabled"];
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { i18n, resolveAppLocale } from "@/i18n";
import { createItineraryPaymentRequest, decideItineraryApproval, fetchItineraryApproval, } from "@/domains/itinerary/repositories/public-itinerary-review.repository";
import { ItineraryApprovalRequestError } from "@/domains/itinerary/services/itinerary-approval-client.service";
import { normalizePublicBrandProfile, } from "@/shared/branding/public-brand";
import { appLanguageOptions } from "@/shared/language/language-preferences";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ItineraryReview',
    setup(__props) {
        const route = useRoute();
        const { t, locale } = useI18n();
        const itineraryId = computed(() => String(route.params.itineraryId ?? ""));
        const approvalGrantId = computed(() => String(route.params.approvalGrantId ?? ""));
        const accessGrant = ref("");
        const review = ref(null);
        const version = ref(null);
        const isLoading = ref(true);
        const isSubmitting = ref(false);
        const isRedirecting = ref(false);
        const error = ref("");
        const retryableError = ref(false);
        const rejectionReason = ref("");
        const checkoutNotice = ref("");
        let paymentPollTimer = null;
        let paymentPollAttempts = 0;
        let referrerMeta = null;
        const languageOptions = appLanguageOptions;
        const brand = computed(() => normalizePublicBrandProfile(review.value?.brand));
        const brandInitial = computed(() => brand.value.name.trim().charAt(0).toUpperCase());
        const supportEmail = computed(() => brand.value.support.email);
        const softBorderColor = computed(() => `${brand.value.colors.accent}66`);
        const subtleBackground = computed(() => `${brand.value.colors.accent}1a`);
        const pageBackground = computed(() => `radial-gradient(circle at top left, ${brand.value.colors.accent}33, transparent 32rem), ${brand.value.colors.background}`);
        const heroBackground = computed(() => `linear-gradient(135deg, ${brand.value.colors.accent}26, #ffffff 54%, ${brand.value.colors.primary}12)`);
        const statusPillStyle = computed(() => ({
            backgroundColor: subtleBackground.value,
            color: brand.value.colors.primary,
        }));
        const effectiveCommunicationLocale = computed(() => review.value?.effectiveLocale || review.value?.locale || null);
        const canRespond = computed(() => review.value?.decision === null);
        // Terminal business state remains readable, but cannot be decided again.
        const canDecide = computed(() => canRespond.value && review.value?.itinerary.state === "pending");
        const payment = computed(() => review.value?.payment ?? null);
        const isPaymentPending = computed(() => payment.value?.status === "checkout_created" ||
            payment.value?.status === "processing");
        const canRetryPayment = computed(() => isPaymentPending.value &&
            payment.value?.status === "checkout_created" &&
            Boolean(payment.value.checkoutUrl));
        const canPayApprovedItinerary = computed(() => review.value?.decision === "approved" &&
            review.value?.itinerary.state === "approved" &&
            !paymentComplete.value);
        const canStartPayment = computed(() => canRetryPayment.value || canPayApprovedItinerary.value);
        const paymentComplete = computed(() => review.value?.itinerary.state === "paid" ||
            payment.value?.status === "succeeded");
        const paymentPanelVisible = computed(() => !paymentComplete.value &&
            (canPayApprovedItinerary.value ||
                isRedirecting.value ||
                isPaymentPending.value ||
                checkoutNotice.value === "return" ||
                checkoutNotice.value === "cancel"));
        const formattedPaymentAmount = computed(() => {
            const amount = payment.value?.amountMinor;
            const currency = payment.value?.currency;
            if (amount === null || amount === undefined || !currency)
                return "";
            return new Intl.NumberFormat(locale.value, {
                style: "currency",
                currency,
            }).format(amount / 100);
        });
        const pricedServices = computed(() => review.value?.itinerary.services?.filter((service) => service.pricing) ?? []);
        const hasPricing = computed(() => pricedServices.value.length > 0 && Boolean(review.value?.itinerary.pricing));
        const paymentPanelTitle = computed(() => {
            if (isRedirecting.value) {
                return t("public_itinerary_review.payment.redirecting_title");
            }
            if (checkoutNotice.value === "return") {
                return t("public_itinerary_review.payment.return_title");
            }
            if (checkoutNotice.value === "cancel") {
                return t("public_itinerary_review.payment.cancel_title");
            }
            return t("public_itinerary_review.payment.pending_title");
        });
        const paymentPanelDescription = computed(() => {
            if (isRedirecting.value) {
                return t("public_itinerary_review.payment.redirecting_body");
            }
            if (checkoutNotice.value === "return") {
                return t("public_itinerary_review.payment.return_body");
            }
            if (checkoutNotice.value === "cancel") {
                return t("public_itinerary_review.payment.cancel_body");
            }
            return t("public_itinerary_review.payment.pending_body");
        });
        const showProcessedMessage = computed(() => !canDecide.value && !paymentPanelVisible.value);
        const processedMessage = computed(() => {
            // Prefer signals scoped to this grant (`decision`) and actual payment completion
            // (`paymentComplete`) over the itinerary's own `state`, which can be set through paths
            // unrelated to this grant (same reasoning as canRespond/canPayApprovedItinerary above).
            if (paymentComplete.value)
                return t("public_itinerary_review.processed.paid");
            if (review.value?.decision === "rejected")
                return t("public_itinerary_review.processed.rejected");
            if (review.value?.decision === "approved")
                return t("public_itinerary_review.processed.approved");
            const state = review.value?.itinerary.state?.toLowerCase();
            if (state === "expired")
                return t("public_itinerary_review.processed.expired");
            if (state === "cancelled") {
                return t("public_itinerary_review.processed.cancelled");
            }
            return t("public_itinerary_review.processed.default");
        });
        function parseHashParams(hash) {
            const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
            const grants = params.getAll("access-grant");
            return { accessGrant: grants.length === 1 && /^ag1_[A-Za-z0-9_-]{86}$/.test(grants[0]) ? grants[0] : "" };
        }
        onMounted(async () => {
            referrerMeta = document.createElement("meta");
            referrerMeta.name = "referrer";
            referrerMeta.content = "no-referrer";
            document.head.appendChild(referrerMeta);
            accessGrant.value = parseHashParams(window.location.hash).accessGrant;
            await loadReview();
            handleCheckoutQuery();
        });
        onBeforeUnmount(() => {
            clearPaymentPoll();
            referrerMeta?.remove();
        });
        function applyReviewContext(context) {
            // Validate before mutating anything — a malformed/incomplete response must never wipe out an
            // already-rendered, still-valid itinerary. Without this check, `review.value = context`
            // executes first and only THEN would `context.version` throw, leaving the page stuck on the
            // corrupted (e.g. null) state even though the error gets caught by the caller.
            if (!context || typeof context.version !== "number" || !context.itinerary) {
                throw new Error("Received an incomplete itinerary-approval context");
            }
            review.value = context;
            version.value = context.version;
            const publicLocale = context.effectiveLocale ?? context.locale;
            if (publicLocale)
                applyPublicLocale(publicLocale);
        }
        async function loadReview(options = {}) {
            if (!options.silent)
                isLoading.value = true;
            error.value = "";
            retryableError.value = false;
            try {
                if (!accessGrant.value) {
                    error.value = route.query.checkout
                        ? t("public_itinerary_review.reopen_email")
                        : t("public_itinerary_review.unavailable");
                    return;
                }
                applyReviewContext(await fetchItineraryApproval(itineraryId.value, approvalGrantId.value, accessGrant.value));
            }
            catch (err) {
                retryableError.value = !(err instanceof ItineraryApprovalRequestError && [401, 403, 404, 410].includes(err.status));
                error.value = t(retryableError.value ? "public_itinerary_review.load_error" : "public_itinerary_review.unavailable");
            }
            finally {
                if (!options.silent)
                    isLoading.value = false;
            }
        }
        async function approve() {
            await decide("approved");
        }
        async function reject() {
            await decide("rejected", rejectionReason.value);
        }
        async function decide(state, reason) {
            if (version.value === null)
                return;
            isSubmitting.value = true;
            error.value = "";
            try {
                applyReviewContext(await decideItineraryApproval(itineraryId.value, approvalGrantId.value, accessGrant.value, {
                    state,
                    version: version.value,
                    reason,
                }));
            }
            catch (err) {
                const isConflict = err instanceof ItineraryApprovalRequestError && err.status === 409;
                error.value = isConflict
                    ? t("public_itinerary_review.conflict")
                    : t("public_itinerary_review.submit_error");
                try {
                    applyReviewContext(await fetchItineraryApproval(itineraryId.value, approvalGrantId.value, accessGrant.value));
                    if (!isConflict)
                        error.value = "";
                }
                catch { /* Keep the last confirmed context and the error. */ }
            }
            finally {
                if (!isRedirecting.value)
                    isSubmitting.value = false;
            }
        }
        function handleCheckoutQuery() {
            const checkout = Array.isArray(route.query.checkout)
                ? route.query.checkout[0]
                : route.query.checkout;
            if (checkout === "return") {
                checkoutNotice.value = "return";
                startPaymentPolling();
                return;
            }
            if (checkout === "cancel") {
                checkoutNotice.value = "cancel";
            }
        }
        async function startPayment() {
            const checkoutUrl = payment.value?.checkoutUrl;
            if (checkoutUrl) {
                redirectToCheckout(checkoutUrl);
                return;
            }
            isSubmitting.value = true;
            error.value = "";
            try {
                const result = await createItineraryPaymentRequest(itineraryId.value, approvalGrantId.value, accessGrant.value);
                if (result.checkoutUrl) {
                    redirectToCheckout(result.checkoutUrl);
                    return;
                }
                await loadReview();
            }
            catch (err) {
                if (err instanceof ItineraryApprovalRequestError && err.status === 503) {
                    error.value = t("public_itinerary_review.payment.unavailable");
                }
                else {
                    error.value = t("public_itinerary_review.submit_error");
                }
            }
            finally {
                if (!isRedirecting.value)
                    isSubmitting.value = false;
            }
        }
        function redirectToCheckout(checkoutUrl) {
            isRedirecting.value = true;
            checkoutNotice.value = "";
            window.location.assign(checkoutUrl);
        }
        function startPaymentPolling() {
            clearPaymentPoll();
            paymentPollAttempts = 0;
            if (paymentComplete.value)
                return;
            schedulePaymentPoll();
        }
        function schedulePaymentPoll() {
            if (paymentPollAttempts >= 12)
                return;
            paymentPollTimer = window.setTimeout(async () => {
                paymentPollAttempts += 1;
                await loadReview({ silent: true });
                if (paymentComplete.value) {
                    clearPaymentPoll();
                    return;
                }
                schedulePaymentPoll();
            }, 2000);
        }
        function clearPaymentPoll() {
            if (paymentPollTimer) {
                window.clearTimeout(paymentPollTimer);
                paymentPollTimer = null;
            }
        }
        function stateLabel(state) {
            const labels = {
                pending: t("common.status.pending"),
                approved: t("common.status.approved"),
                rejected: t("common.status.rejected"),
                paid: t("common.status.paid"),
                cancelled: t("common.status.cancelled"),
                expired: t("common.status.expired"),
            };
            return labels[state.toLowerCase()] ?? state;
        }
        function applyPublicLocale(value) {
            const next = resolveAppLocale(value);
            locale.value = next;
            i18n.global.locale.value = next;
            document.documentElement.lang = next;
        }
        function languageButtonStyle(value) {
            const active = locale.value === value;
            return active
                ? {
                    backgroundColor: brand.value.colors.primary,
                    color: "#ffffff",
                }
                : {
                    color: brand.value.colors.text,
                };
        }
        function formatMoney(value) {
            const amount = Number(value ?? 0);
            return new Intl.NumberFormat(locale.value, {
                style: "currency",
                currency: review.value?.payment?.currency ?? "USD",
            }).format(Number.isFinite(amount) ? amount : 0);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("main", {
                class: "itinerary-review-shell min-h-screen overflow-hidden px-4 py-6 sm:py-10",
                style: _normalizeStyle({
                    background: pageBackground.value,
                    color: brand.value.colors.text,
                    fontFamily: brand.value.fontFamily,
                })
            }, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", {
                        class: "itinerary-review-orb -left-20 top-16",
                        style: _normalizeStyle({ backgroundColor: brand.value.colors.primary })
                    }, null, 4),
                    _createElementVNode("div", {
                        class: "itinerary-review-orb right-0 top-48",
                        style: _normalizeStyle({ backgroundColor: brand.value.colors.accent })
                    }, null, 4)
                ]),
                _createElementVNode("section", {
                    class: "itinerary-review-card relative mx-auto flex max-w-3xl flex-col overflow-hidden rounded-[2rem] border bg-white/95 shadow-2xl shadow-eblack-950/10 backdrop-blur",
                    style: _normalizeStyle({ borderColor: softBorderColor.value })
                }, [
                    _createElementVNode("header", {
                        class: "relative overflow-hidden border-b p-6 sm:p-8",
                        style: _normalizeStyle({ borderColor: softBorderColor.value, background: heroBackground.value })
                    }, [
                        _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("div", _hoisted_3, [
                                (brand.value.logoUrl)
                                    ? (_openBlock(), _createElementBlock("img", {
                                        key: 0,
                                        src: brand.value.logoUrl,
                                        alt: _unref(t)('public_itinerary_review.brand_logo_alt', {
                                            brand: brand.value.name,
                                        }),
                                        class: "h-12 w-12 rounded-2xl bg-white/80 object-contain p-1 shadow-sm"
                                    }, null, 8, _hoisted_4))
                                    : (_openBlock(), _createElementBlock("div", {
                                        key: 1,
                                        class: "flex h-12 w-12 items-center justify-center rounded-2xl text-base font-bold text-white shadow-sm",
                                        style: _normalizeStyle({ backgroundColor: brand.value.colors.primary }),
                                        "aria-hidden": "true"
                                    }, _toDisplayString(brandInitial.value), 5)),
                                _createElementVNode("div", _hoisted_5, [
                                    _createElementVNode("p", {
                                        class: "truncate text-sm font-semibold",
                                        style: _normalizeStyle({ color: brand.value.colors.primary })
                                    }, _toDisplayString(brand.value.name), 5),
                                    _createElementVNode("h1", _hoisted_6, _toDisplayString(_unref(t)("public_itinerary_review.title")), 1)
                                ])
                            ]),
                            _createElementVNode("div", _hoisted_7, [
                                _createElementVNode("div", _hoisted_8, [
                                    (supportEmail.value)
                                        ? (_openBlock(), _createElementBlock("a", {
                                            key: 0,
                                            href: `mailto:${supportEmail.value}`,
                                            class: "rounded-full border bg-white/70 px-3.5 py-2 text-sm font-semibold shadow-sm transition hover:-translate-y-0.5 hover:bg-white",
                                            style: _normalizeStyle({
                                                borderColor: softBorderColor.value,
                                                color: brand.value.colors.text,
                                            })
                                        }, _toDisplayString(_unref(t)("public_itinerary_review.support")), 13, _hoisted_9))
                                        : _createCommentVNode("", true),
                                    _createElementVNode("div", {
                                        class: "flex rounded-full border bg-white/70 p-1 shadow-sm",
                                        style: _normalizeStyle({ borderColor: softBorderColor.value })
                                    }, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(languageOptions), (option) => {
                                            return (_openBlock(), _createElementBlock("button", {
                                                key: option.value,
                                                type: "button",
                                                class: "rounded-full px-3 py-1.5 text-xs font-semibold transition",
                                                style: _normalizeStyle(languageButtonStyle(option.value)),
                                                onClick: ($event) => (applyPublicLocale(option.value))
                                            }, _toDisplayString(option.label), 13, _hoisted_10));
                                        }), 128))
                                    ], 4)
                                ])
                            ])
                        ])
                    ], 4),
                    _createElementVNode("div", _hoisted_11, [
                        (isLoading.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                (_openBlock(), _createElementBlock("svg", {
                                    class: "h-9 w-9 animate-spin",
                                    style: _normalizeStyle({ color: brand.value.colors.primary }),
                                    xmlns: "http://www.w3.org/2000/svg",
                                    fill: "none",
                                    viewBox: "0 0 24 24",
                                    "aria-hidden": "true"
                                }, [...(_cache[2] || (_cache[2] = [
                                        _createElementVNode("circle", {
                                            class: "opacity-25",
                                            cx: "12",
                                            cy: "12",
                                            r: "10",
                                            stroke: "currentColor",
                                            "stroke-width": "4"
                                        }, null, -1),
                                        _createElementVNode("path", {
                                            class: "opacity-75",
                                            fill: "currentColor",
                                            d: "M4 12a8 8 0 018-8v8z"
                                        }, null, -1)
                                    ]))], 4)),
                                _createElementVNode("p", null, _toDisplayString(_unref(t)("public_itinerary_review.loading")), 1)
                            ]))
                            : (error.value && !review.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                    _createTextVNode(_toDisplayString(error.value) + " ", 1),
                                    (retryableError.value)
                                        ? (_openBlock(), _createElementBlock("button", {
                                            key: 0,
                                            type: "button",
                                            class: "ml-2 underline",
                                            onClick: _cache[0] || (_cache[0] = ($event) => (loadReview()))
                                        }, _toDisplayString(_unref(t)("public_itinerary_review.retry")), 1))
                                        : _createCommentVNode("", true)
                                ]))
                                : (review.value)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                                        (error.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_14, _toDisplayString(error.value), 1))
                                            : _createCommentVNode("", true),
                                        _createElementVNode("div", {
                                            class: "itinerary-review-fade rounded-3xl border p-5 sm:p-6",
                                            style: _normalizeStyle({
                                                borderColor: softBorderColor.value,
                                                backgroundColor: subtleBackground.value,
                                            })
                                        }, [
                                            _createElementVNode("div", _hoisted_15, [
                                                _createElementVNode("div", null, [
                                                    _createElementVNode("p", _hoisted_16, _toDisplayString(_unref(t)("public_itinerary_review.itinerary")), 1),
                                                    _createElementVNode("p", _hoisted_17, _toDisplayString(review.value.itinerary.code), 1),
                                                    (review.value.itinerary.customer?.name)
                                                        ? (_openBlock(), _createElementBlock("p", _hoisted_18, _toDisplayString(review.value.itinerary.customer.name), 1))
                                                        : _createCommentVNode("", true)
                                                ]),
                                                _createElementVNode("span", {
                                                    class: "inline-flex w-fit rounded-full px-3.5 py-2 text-xs font-bold uppercase tracking-wide",
                                                    style: _normalizeStyle(statusPillStyle.value)
                                                }, _toDisplayString(stateLabel(review.value.itinerary.state)), 5)
                                            ])
                                        ], 4),
                                        (review.value.itinerary.services?.length)
                                            ? (_openBlock(), _createElementBlock("div", {
                                                key: 1,
                                                class: "itinerary-review-fade rounded-3xl border bg-white p-5 sm:p-6",
                                                style: _normalizeStyle({ borderColor: softBorderColor.value })
                                            }, [
                                                _createElementVNode("p", _hoisted_19, _toDisplayString(_unref(t)("public_itinerary_review.included_services")), 1),
                                                _createElementVNode("ul", _hoisted_20, [
                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(review.value.itinerary.services, (service) => {
                                                        return (_openBlock(), _createElementBlock("li", {
                                                            key: `${service.name}-${service.quantityOfParticipants}`,
                                                            class: "rounded-2xl border bg-ewhite-50/70 p-4 transition hover:-translate-y-0.5 hover:shadow-md",
                                                            style: _normalizeStyle({ borderColor: softBorderColor.value })
                                                        }, [
                                                            _createElementVNode("span", _hoisted_21, _toDisplayString(service.name), 1),
                                                            _createElementVNode("span", _hoisted_22, _toDisplayString(_unref(t)("public_itinerary_review.participants", {
                                                                count: service.quantityOfParticipants,
                                                            })), 1)
                                                        ], 4));
                                                    }), 128))
                                                ])
                                            ], 4))
                                            : _createCommentVNode("", true),
                                        (hasPricing.value)
                                            ? (_openBlock(), _createElementBlock("div", {
                                                key: 2,
                                                class: "itinerary-review-fade rounded-3xl border bg-white p-5 sm:p-6",
                                                style: _normalizeStyle({ borderColor: softBorderColor.value })
                                            }, [
                                                _createElementVNode("div", _hoisted_23, [
                                                    _createElementVNode("div", null, [
                                                        _createElementVNode("p", _hoisted_24, _toDisplayString(_unref(t)("public_itinerary_review.pricing.title")), 1),
                                                        _createElementVNode("p", _hoisted_25, _toDisplayString(_unref(t)("public_itinerary_review.pricing.visible_notice")), 1)
                                                    ]),
                                                    _createElementVNode("p", {
                                                        class: "text-lg font-black",
                                                        style: _normalizeStyle({ color: brand.value.colors.primary })
                                                    }, _toDisplayString(formatMoney(review.value.itinerary.pricing?.totalAmount)), 5)
                                                ]),
                                                _createElementVNode("div", _hoisted_26, [
                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pricedServices.value, (service) => {
                                                        return (_openBlock(), _createElementBlock("div", {
                                                            key: `${service.name}-${service.quantityOfParticipants}-pricing`,
                                                            class: "rounded-2xl border p-4",
                                                            style: _normalizeStyle({ borderColor: softBorderColor.value })
                                                        }, [
                                                            _createElementVNode("div", _hoisted_27, [
                                                                _createElementVNode("span", null, _toDisplayString(service.name), 1),
                                                                _createElementVNode("span", null, _toDisplayString(formatMoney(service.pricing?.totalAmount)), 1)
                                                            ]),
                                                            _createElementVNode("dl", _hoisted_28, [
                                                                _createElementVNode("div", _hoisted_29, [
                                                                    _createElementVNode("dt", null, _toDisplayString(_unref(t)("public_itinerary_review.pricing.service_subtotal")), 1),
                                                                    _createElementVNode("dd", null, _toDisplayString(formatMoney(service.pricing?.serviceSubtotal)), 1)
                                                                ]),
                                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(service.pricing?.charges ?? [], (charge) => {
                                                                    return (_openBlock(), _createElementBlock("div", {
                                                                        key: `${service.name}-${charge.name}`,
                                                                        class: "flex justify-between gap-3"
                                                                    }, [
                                                                        _createElementVNode("dt", null, _toDisplayString(charge.name), 1),
                                                                        _createElementVNode("dd", null, _toDisplayString(formatMoney(charge.amount)), 1)
                                                                    ]));
                                                                }), 128)),
                                                                _createElementVNode("div", _hoisted_30, [
                                                                    _createElementVNode("dt", null, _toDisplayString(_unref(t)("public_itinerary_review.pricing.tax")), 1),
                                                                    _createElementVNode("dd", null, _toDisplayString(formatMoney(service.pricing?.taxAmount)), 1)
                                                                ])
                                                            ])
                                                        ], 4));
                                                    }), 128))
                                                ])
                                            ], 4))
                                            : _createCommentVNode("", true),
                                        (review.value.pdfUrl)
                                            ? (_openBlock(), _createElementBlock("a", {
                                                key: 3,
                                                href: review.value.pdfUrl,
                                                target: "_blank",
                                                rel: "noopener noreferrer",
                                                class: "itinerary-review-fade inline-flex justify-center rounded-full border px-5 py-3 text-sm font-bold transition hover:-translate-y-0.5 hover:bg-eorange-50",
                                                style: _normalizeStyle({
                                                    borderColor: brand.value.colors.primary,
                                                    color: brand.value.colors.primary,
                                                })
                                            }, _toDisplayString(_unref(t)("public_itinerary_review.view_pdf")), 13, _hoisted_31))
                                            : _createCommentVNode("", true),
                                        (paymentPanelVisible.value)
                                            ? (_openBlock(), _createElementBlock("div", {
                                                key: 4,
                                                class: "itinerary-review-fade rounded-3xl border p-5 text-sm sm:p-6",
                                                style: _normalizeStyle({
                                                    borderColor: softBorderColor.value,
                                                    backgroundColor: subtleBackground.value,
                                                })
                                            }, [
                                                _createElementVNode("p", {
                                                    class: "text-lg font-black",
                                                    style: _normalizeStyle({ color: brand.value.colors.text })
                                                }, _toDisplayString(paymentPanelTitle.value), 5),
                                                _createElementVNode("p", _hoisted_32, _toDisplayString(paymentPanelDescription.value), 1),
                                                (formattedPaymentAmount.value)
                                                    ? (_openBlock(), _createElementBlock("p", {
                                                        key: 0,
                                                        class: "mt-2 text-sm font-semibold",
                                                        style: _normalizeStyle({ color: brand.value.colors.primary })
                                                    }, _toDisplayString(_unref(t)("public_itinerary_review.payment.amount_due", {
                                                        amount: formattedPaymentAmount.value,
                                                    })), 5))
                                                    : _createCommentVNode("", true),
                                                (canStartPayment.value)
                                                    ? (_openBlock(), _createElementBlock("button", {
                                                        key: 1,
                                                        type: "button",
                                                        class: "mt-4 inline-flex justify-center rounded-full px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 disabled:opacity-60",
                                                        style: _normalizeStyle({ backgroundColor: brand.value.colors.primary }),
                                                        disabled: isSubmitting.value || isRedirecting.value,
                                                        onClick: startPayment
                                                    }, _toDisplayString(_unref(t)("public_itinerary_review.payment.retry")), 13, _hoisted_33))
                                                    : _createCommentVNode("", true)
                                            ], 4))
                                            : _createCommentVNode("", true),
                                        (canDecide.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_34, [
                                                _withDirectives(_createElementVNode("textarea", {
                                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((rejectionReason).value = $event)),
                                                    class: "min-h-24 rounded-2xl border-ewhite-300 text-sm focus:border-eorange-400 focus:ring-eorange-400",
                                                    placeholder: _unref(t)('public_itinerary_review.rejection_placeholder')
                                                }, null, 8, _hoisted_35), [
                                                    [
                                                        _vModelText,
                                                        rejectionReason.value,
                                                        void 0,
                                                        { trim: true }
                                                    ]
                                                ]),
                                                _createElementVNode("div", _hoisted_36, [
                                                    _createElementVNode("button", {
                                                        type: "button",
                                                        class: "inline-flex flex-1 justify-center rounded-full px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 disabled:opacity-60",
                                                        style: _normalizeStyle({ backgroundColor: brand.value.colors.success }),
                                                        disabled: isSubmitting.value || isRedirecting.value,
                                                        onClick: approve
                                                    }, _toDisplayString(isRedirecting.value
                                                        ? _unref(t)("public_itinerary_review.payment.redirecting")
                                                        : _unref(t)("public_itinerary_review.approve")), 13, _hoisted_37),
                                                    _createElementVNode("button", {
                                                        type: "button",
                                                        class: "inline-flex flex-1 justify-center rounded-full px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 disabled:opacity-60",
                                                        style: _normalizeStyle({ backgroundColor: brand.value.colors.primary }),
                                                        disabled: isSubmitting.value || isRedirecting.value,
                                                        onClick: reject
                                                    }, _toDisplayString(_unref(t)("public_itinerary_review.reject")), 13, _hoisted_38)
                                                ])
                                            ]))
                                            : (showProcessedMessage.value)
                                                ? (_openBlock(), _createElementBlock("div", {
                                                    key: 6,
                                                    class: "itinerary-review-fade rounded-3xl p-5 text-sm font-semibold",
                                                    style: _normalizeStyle({
                                                        backgroundColor: subtleBackground.value,
                                                        color: brand.value.colors.text,
                                                    })
                                                }, _toDisplayString(processedMessage.value), 5))
                                                : _createCommentVNode("", true)
                                    ], 64))
                                    : _createCommentVNode("", true)
                    ])
                ], 4)
            ], 4));
        };
    }
});
